import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { Container, ListJob, Title, Wrapper } from './JobYouWillLove.css'

import { getJobYouWillLove, getSpecialJob, trackImpression } from './services'
import { TQuery, TUTM, TYPE } from './type'
import { ELanguage } from '../Utils/constants'
import translations from './translations'
import JobCardMini from '../JobCardMini'
import { EVariant, TJobMiniInfo } from '../JobCardMini/type'
import { buildCompanyUrl, buildJobUrl } from '../Utils/urlBuilder'
import Button from '../Button'
import { TAuthUser } from '../QuickApplyJobModal/type'
import { TInitData } from '../ApplyJobs/store/provider'
import useElementOnScreen from '../Utils/hooks/UseElementOnScreen'
import { filterPrivateIP } from '../Utils/helpers'

export type TTrackingImpressionSection =
    | 'none'
    | 'jobsYouWillLove_successApply'
    | 'jobsYouWillLove_jobDetail'
    | 'similarJobsForYou_jobDetail'

export type TTrackingImpressionData = {
    clientIP: string[]
    userID: string
    jobID: number
}

export type TJobYouWillLoveP = {
    type?: TYPE
    typeOptions?: {
        isShowSkill?: boolean
        variant?: EVariant
        gridColumn?: number
        gridMobileColumn?: number
    }
    api: {
        baseUrl: string
        accessToken?: string
        query?: TQuery
        appId: string
        source: string
        smartNaviVersion?: string
        formDataExcludeJobIds?: FormData
    }
    robot?: {
        enable: boolean
        data: any[]
        source?: string
    }
    utmType?: TUTM
    utmString?: string
    langCode: ELanguage
    previewJob?: {
        enable: boolean
        appMode?: string
        utmSource?: string
        baseUrl: string
    }
    title?: string
    titleNode?: ReactNode
    isMobile: boolean
    isShowApplyAll?: boolean
    classSelector?: string
    userInfo?: TAuthUser
    ignoreJobIds?: number[]
    getJobIds?: (value: number[]) => void
    initData: TInitData
    enableTrackingImpression?: boolean
    trackingImpressionSection?: TTrackingImpressionSection
    trackingData?: TTrackingImpressionData | null
}

type TDataJob = {
    jobs: TJobMiniInfo[]
    source: string
}

const JobYouWillLove = (props: TJobYouWillLoveP) => {
    const {
        typeOptions,
        api,
        langCode,
        utmType,
        utmString,
        robot,
        previewJob,
        title = '',
        isMobile,
        isShowApplyAll,
        classSelector = '',
        titleNode,
        userInfo,
        ignoreJobIds = [],
        getJobIds: getJobIdsProps,
        enableTrackingImpression = false,
        trackingImpressionSection = 'none',
        trackingData = null
    } = props
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState<TDataJob>({
        jobs: [],
        source: ''
    })
    const isCalledTracking = useRef<boolean>(false)
    const [jobYouWillLoveWrapperState, setJobRef] = useState<HTMLDivElement | null>(null)

    const [isElementOnScreen] = useElementOnScreen({
        target: jobYouWillLoveWrapperState,
        options: {
            isUseReactRef: false,
            rootMargin: '0px 0px 0px 0px'
        }
    })

    const isLoading = robot?.enable ? false : loading

    const t = (key: any): string => {
        if (langCode === ELanguage.EN) {
            return key
        } else {
            return translations[key] || key
        }
    }

    const createdUtm = (type: string, source: string) => {
        switch (type) {
            case 'my-profile':
                return `/?utm_source_navi=CareerCenter&utm_medium_navi=MyProfile&utm_campaign_navi=JobsYouWillLove&utm_content_navi=${source}&utm_term_navi=VB`
            case 'job-detail':
                return `/?utm_source_navi=jobalertemail&utm_medium_navi=JobDescription&utm_campaign_navi=PeopleAlsoView&utm_content_navi=${source}&utm_term_navi=VB`
            case 'job-detail-right-corner':
                return `/?utm_source_navi=jobdetail&utm_medium_navi=rightcorner&utm_campaign_navi=relevantjobs&utm_content_navi=${source}`
            case 'event':
                return `/?utm_source_navi=eventPage&utm_medium_navi=rightcorner&utm_campaign_navi=relevantjobs&utm_content_navi=${source}`
            case 'competitive':
                return `?utm_source_navi=competitive&utm_medium_navi=job-will-love&utm_campaign_navi=result&utm_content_navi=${source}`
            case 'single-success-apply':
                return `?utm_source_navi=success-apply-single&utm_medium_navi=job-will-love&utm_campaign_navi=bottom&utm_content_navi=${source}`
            default:
                return utmString ? `${utmString}&utm_content_navi=${source}` : ''
        }
    }

    const formatJobs = (jobs: any[], source: string, ignoreJobIds: number[]) => {
        const jobUnIgnore = (jobs || []).filter((job) => !ignoreJobIds.includes(job.jobId))
        return jobUnIgnore.map((item: any) => {
            let locationText = ''
            if (item.workingLocations) {
                locationText = (item.workingLocations || [])
                    .map((item: any) => (langCode === ELanguage.VI ? item.cityNameVI : item.cityName))
                    .join(', ')
            } else if (item.locations) {
                locationText =
                    langCode === ELanguage.VI ? item.locations['1'].join(', ') : item.locations['2'].join(', ')
            }
            return {
                ...item,
                jobTitle: item.jobTitle,
                jobId: item.jobId,
                jobUrl:
                    item.jobLink ||
                    buildJobUrl({
                        alias: item.alias,
                        jobId: item.jobId,
                        langCode,
                        utm: createdUtm(utmType || '', source)
                    }),
                companyLogo: item.companyLogo,
                company: item.company,
                companyUrl: buildCompanyUrl({ companyId: item.companyId, companySlug: item.company, langCode }),
                jobLevelText: langCode === ELanguage.VI ? item.jobLevelVI : item.jobLevel,
                jobLevelId: item.jobLevelId,
                address: item.address,
                jobDescription: item.jobDescription,
                jobRequirement: item.jobRequirement,
                isAnonymous: item.isAnonymous,
                locationText: locationText,
                prettySalary: item.prettySalary
            }
        })
    }

    const getJobs = async () => {
        let clientIP = ''
        if (typeof trackingData?.clientIP === 'string') {
            clientIP = trackingData?.clientIP
        } else if (
            trackingData?.clientIP &&
            typeof trackingData?.clientIP === 'object' &&
            trackingData?.clientIP.length > 0
        ) {
            clientIP = trackingData?.clientIP?.join(',')
        }

        const query = {
            jobId: '0',
            ...api.query,
            clientIP: clientIP
        }
        if (trackingImpressionSection !== 'none') {
            query.section = trackingImpressionSection
        }
        const response = await getJobYouWillLove({
            baseUrl: api.baseUrl,
            langCode: langCode,
            query: query,
            smartNaviVersion: api.smartNaviVersion || 'v2',
            formDataExcludeJobIds: api.formDataExcludeJobIds
        })
        const { data } = response
        const newData = formatJobs(data.jobs, data.source, ignoreJobIds)
        if (api?.accessToken && newData.length > 0) {
            const { data: jobs } = await getSpecialJob({
                baseUrl: api.baseUrl,
                accessToken: api.accessToken,
                jobs: newData
            })
            setData({
                jobs,
                source: data.source
            })
        } else {
            setData({
                jobs: newData,
                source: data.source
            })
        }
        setLoading(false)
    }

    const memoJobIds = useMemo(() => {
        if (!data.jobs || data.jobs.length < 1) return null
        const jobIds = data.jobs.map((job) => {
            return job.jobId
        })
        return jobIds
    }, [data.jobs])

    useEffect(() => {
        if (!robot?.enable) {
            getJobs()
        }
    }, [])

    useEffect(() => {
        if (!isElementOnScreen || !enableTrackingImpression || isCalledTracking.current) {
            return
        }
        let clientIP = ''
        if (typeof trackingData?.clientIP === 'string') {
            clientIP = trackingData?.clientIP
            const splittedClientIPs = clientIP.split(',').map((ip) => ip.trim())
            const filterPublic = filterPrivateIP(splittedClientIPs)
            clientIP = filterPublic.length > 0 ? filterPublic[0] : clientIP
        } else if (
            trackingData?.clientIP &&
            typeof trackingData?.clientIP === 'object' &&
            trackingData?.clientIP.length > 0
        ) {
            const filterPublic = filterPrivateIP(trackingData?.clientIP)
            clientIP = filterPublic.length > 0 ? filterPublic[0] : trackingData?.clientIP?.join(',')
        }

        const jobIds: number[] = data.jobs.map((jobItem: any) => jobItem.jobId)
        trackImpression({
            baseMsUrl: api.baseUrl,
            langCode: langCode,
            body: {
                section: trackingImpressionSection,
                jobRecommend: jobIds,
                version: data.source,
                clientIP: clientIP,
                jobID: trackingData?.jobID ?? -1,
                userID: trackingData?.userID ?? ''
            }
        })
        isCalledTracking.current = true
    }, [jobYouWillLoveWrapperState, isLoading, isElementOnScreen, data, enableTrackingImpression, isCalledTracking])

    useEffect(() => {
        if (memoJobIds && memoJobIds?.length > 0) {
            getJobIdsProps && getJobIdsProps(memoJobIds)
        }
    }, [memoJobIds, getJobIdsProps])

    const titleSection = title || t('Jobs You Will Love')

    const jobs = robot?.enable ? formatJobs(robot?.data, robot?.source || '', ignoreJobIds) : data.jobs

    return !isLoading && jobs && jobs?.length > 0 ? (
        <Container className='job-you-will-love' ref={(ref) => setJobRef(ref)}>
            <Wrapper className='jobs-section'>
                {titleNode || <Title className='title'>{titleSection}</Title>}

                <ListJob
                    gridColumn={typeOptions?.gridColumn}
                    gridMobileColumn={typeOptions?.gridMobileColumn}
                    isMobile={isMobile}
                >
                    {jobs.map((item, idx) => {
                        return (
                            <JobCardMini
                                key={idx}
                                isMobile={isMobile}
                                item={item}
                                langCode={langCode}
                                api={api}
                                previewJob={previewJob}
                                classSelector={classSelector}
                                userInfo={userInfo}
                                settingJob={{
                                    isShowSkills: typeOptions?.isShowSkill ?? false,
                                    variant: typeOptions?.variant
                                }}
                                initData={props.initData}
                            />
                        )
                    })}
                </ListJob>

                {isShowApplyAll && data.jobs.length > 0 && (
                    <Button
                        type='outline'
                        onClick={() =>
                            (window.location.href =
                                `${window.location.origin}/${t('apply-multiple-jobs')}` +
                                '/' +
                                data.jobs.map((item: any) => item.jobId).join('_') +
                                createdUtm(utmType || '', data.source))
                        }
                        style={{ marginTop: 16, marginLeft: 'auto', display: 'block' }}
                    >
                        {t('Apply All Jobs')}
                    </Button>
                )}
            </Wrapper>
        </Container>
    ) : (
        <></>
    )
}

export default JobYouWillLove
